// js/app/features/charts/index.js
// eslint-disable-next-line max-len
import { 
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    PointElement,
    LineController,
    ArcElement,
    PieController,
    Tooltip,
    Legend,
} from 'chart.js';

// register needed components
Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    BarController,
    LineElement,
    PointElement,
    LineController,
    ArcElement,
    PieController,
    Tooltip,
    Legend,
);

// safe JSON parse helper
function safeParse(raw) {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        console.error('Invalid chart JSON:', e, raw);
        return null;
    }
}

function normalizePayload(payload) {
    // Accept either:
    // eslint-disable-next-line max-len
    // 1) full Chart.js-like: { labels: [...], datasets: [ { data: [...], backgroundColor: [...] } ] }
    // 2) shorthand: { labels: [...], data: [...], backgroundColor: [...] }
    if (!payload) return null;

    if (Array.isArray(payload.datasets) && payload.datasets.length) {
        // already Chart.js shaped
        return { labels: payload.labels || [], datasets: payload.datasets };
    }

    // shorthand -> convert to datasets
    const ds = {
        label: payload.label || '',
        data: payload.data || [],
        backgroundColor: payload.backgroundColor || []
    };
    return { labels: payload.labels || [], datasets: [ds] };
}

function ensureBackgroundColors(dataset) {
    // if no backgroundColor provided, supply a small default palette (repeats if needed)
    if (Array.isArray(dataset.backgroundColor) && dataset.backgroundColor.length) return;
    const defaultPalette = ['#3A70A9', '#E57373', '#81C784', '#FFD54F', '#BA68C8', '#FFB74D'];
    const count = (dataset.data && dataset.data.length) || 0;
    // eslint-disable-next-line max-len
    dataset.backgroundColor = Array.from({ length: count }, (_, i) => defaultPalette[i % defaultPalette.length]);
}

function createChartFromCanvas(canvas) {
    if (!canvas) return null;

    // if a Chart instance already exists here, return it (avoid duplicates)
    if (Chart.getChart) {
        const existing = Chart.getChart(canvas);
        if (existing) {
            return existing;
        }
    } else if (canvas._chart) {
        return canvas._chart;
    }

    const raw = canvas.dataset.chart;
    const parsed = safeParse(raw);
    if (!parsed) return null;

    const norm = normalizePayload(parsed);
    if (!norm) return null;

    // eslint-disable-next-line max-len
    // ensure each dataset has background colors for pie/doughnut; for bar you may want border/background
    norm.datasets.forEach(ensureBackgroundColors);

    const ctx = canvas.getContext('2d');

    const chart = new Chart(ctx, {
        type: canvas.dataset.type || 'pie',
        data: {
            labels: norm.labels,
            datasets: norm.datasets,
        },
        options: {
            animation: {
                duration: 500,
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { position: 'top' },
                tooltip: { enabled: true },
            },
        },
    });

    // store a reference on the canvas (and return it)
    canvas._chart = chart;
    return chart;
}

export function initCharts(root = document) {
    const canvases = Array.from(root.querySelectorAll('canvas[data-chart]'));
    const instances = canvases.map(createChartFromCanvas).filter(Boolean);
    return instances;
}

// Auto-init on full page load
if (typeof window !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => {
        initCharts(document);
    });
}
